import type { QueueColumnDef } from "./queueColumnConfig";
import type { PendingWorkView } from "../../types/pendingWork";

export const PENDING_WORK_VIEW_KEY = "pending_work_current_view_v1";
export const PENDING_WORK_DEFAULT_KEY = "pending_work_default_view_v1";
export const PENDING_WORK_COLUMNS: QueueColumnDef[] = [
  {
    id: "caseNumber",
    label: "Case #",
    defaultVisible: true,
    sortable: true,
    width: 160,
  },
  {
    id: "patient",
    label: "Patient",
    defaultVisible: true,
    sortable: true,
    width: 190,
  },
  { id: "tat", label: "TAT", defaultVisible: true, sortable: true, width: 100 },
  {
    id: "priority",
    label: "Priority",
    defaultVisible: true,
    sortable: true,
    width: 100,
  },
  {
    id: "step",
    label: "Current Status",
    defaultVisible: true,
    sortable: true,
    width: 180,
  },
  {
    id: "pendingTests",
    label: "Pending Slides",
    defaultVisible: true,
    sortable: true,
    width: 120,
  },
  {
    id: "sendouts",
    label: "Send-outs",
    defaultVisible: true,
    sortable: true,
    width: 100,
  },
  {
    id: "otherWork",
    label: "Other Work",
    defaultVisible: true,
    sortable: true,
    width: 100,
  },
  {
    id: "dateReceived",
    label: "Date Received",
    defaultVisible: false,
    sortable: true,
    width: 140,
  },
];
export const PENDING_WORK_KINDS = [
  "failed_qc",
  "slide",
  "sendout",
  "lab",
  "review",
  "correction",
  "qa",
];
export const DEFAULT_PENDING_WORK_VIEW: PendingWorkView = {
  version: 1,
  filters: { prefixKeys: [], search: "", cause: "" },
  sorts: [{ column: "tat", direction: "desc" }],
  columns: PENDING_WORK_COLUMNS.filter((column) => column.defaultVisible).map(
    (column) => column.id
  ),
  overdueThresholdDays: 2,
};
const retiredCauses = new Set(["discussion", "addendum", "amendment"]);
const columns = new Set(PENDING_WORK_COLUMNS.map((column) => column.id));
function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === "object" && raw !== null && !Array.isArray(raw);
}

export function decodePendingWorkView(
  raw: unknown,
  prefixOptions?: string[]
): PendingWorkView | undefined {
  if (
    !isRecord(raw) ||
    raw.version !== 1 ||
    Object.keys(raw).some(
      (key) =>
        ![
          "version",
          "filters",
          "sorts",
          "columns",
          "overdueThresholdDays",
        ].includes(key)
    )
  )
    return undefined;
  if (
    !validFilters(raw.filters, prefixOptions) ||
    !validColumns(raw.columns) ||
    !validSorts(raw.sorts)
  )
    return undefined;
  if (
    typeof raw.overdueThresholdDays !== "number" ||
    !Number.isInteger(raw.overdueThresholdDays) ||
    raw.overdueThresholdDays < 1 ||
    raw.overdueThresholdDays > 30
  )
    return undefined;
  const view = raw as unknown as PendingWorkView;
  return retiredCauses.has(view.filters.cause)
    ? { ...view, filters: { ...view.filters, cause: "" } }
    : view;
}
function validFilters(raw: unknown, options?: string[]): boolean {
  if (
    !isRecord(raw) ||
    Object.keys(raw).some(
      (key) => !["prefixKeys", "search", "cause"].includes(key)
    )
  )
    return false;
  return (
    typeof raw.search === "string" &&
    raw.search.length <= 120 &&
    !raw.search.split("").some((character) => character.charCodeAt(0) < 32) &&
    typeof raw.cause === "string" &&
    (!raw.cause ||
      retiredCauses.has(raw.cause) ||
      PENDING_WORK_KINDS.includes(raw.cause)) &&
    Array.isArray(raw.prefixKeys) &&
    raw.prefixKeys.length <= 32 &&
    new Set(raw.prefixKeys).size === raw.prefixKeys.length &&
    raw.prefixKeys.every(
      (key) =>
        typeof key === "string" &&
        /^([A-Z]{1,16}|__OTHER__)$/.test(key) &&
        (!options || options.includes(key))
    )
  );
}
function validColumns(raw: unknown): boolean {
  return (
    Array.isArray(raw) &&
    raw.length > 0 &&
    raw.every((column) => columns.has(column)) &&
    new Set(raw).size === raw.length
  );
}
function validSorts(raw: unknown): boolean {
  return (
    Array.isArray(raw) &&
    raw.length <= columns.size &&
    new Set(raw.map((sort) => sort?.column)).size === raw.length &&
    raw.every(
      (sort) =>
        isRecord(sort) &&
        Object.keys(sort).length === 2 &&
        typeof sort.column === "string" &&
        columns.has(sort.column) &&
        ["asc", "desc"].includes(String(sort.direction))
    )
  );
}
